import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {UnderlineNav} from '@primer/react'
import {useEffect, useState} from 'react'

import type {LocalNavigationTab, ReactNavTarget, TurboNavAttrs} from '../types'
import {getOcticon} from '../utils/octicons'
import styles from './LocalNavigation.module.css'

export interface LocalNavigationProps {
  tabs?: LocalNavigationTab[]
  selectedLink?: string
}

function getTabCount(count: number | undefined) {
  if (count !== undefined && count > 0) {
    if (count >= 5000) {
      return '5k+'
    }
    if (count >= 1000) {
      return `${parseFloat((count / 1000).toFixed(1))}k`
    }
    return count.toString()
  }
}

/** Extracts react-specific navigation attributes from the context region payload */
function getReactNavAttrs(target: ReactNavTarget | undefined): Record<string, string | undefined> {
  if (!target) {
    return {}
  }

  return {
    'data-react-nav': target.appTarget,
  }
}

function getTurboNavAttrs(target: TurboNavAttrs | undefined): Record<string, string | undefined> {
  if (!target) {
    return {}
  }

  return {
    'data-turbo-frame': target.frame,
  }
}

function findActiveTabBySelectedLink(
  tabs?: LocalNavigationTab[],
  selectedLink?: string,
): LocalNavigationTab | undefined {
  if (!tabs || tabs.length === 0) {
    return undefined
  }

  const currentPath = ssrSafeLocation.pathname + ssrSafeLocation.search

  // First, try to find a tab matching the selectedLink
  if (selectedLink) {
    const selectedLinkTab = tabs.find(tab => tab.selectedLinks?.includes(selectedLink))
    if (selectedLinkTab) {
      return selectedLinkTab
    }
  }

  // If there's no selectedLink match, try to infer the active tab based on the current path
  const currentPathLower = currentPath.toLowerCase()
  const urlMatchedTabs = tabs.filter(tab => {
    const hrefLower = tab.href?.toLowerCase()
    return (
      hrefLower &&
      (currentPathLower === hrefLower ||
        currentPathLower.startsWith(`${hrefLower}/`) ||
        currentPathLower.startsWith(`${hrefLower}?`))
    )
  })

  // Return the tab with the longest matching href (most specific match)
  return urlMatchedTabs.sort((a, b) => (b.href?.length || 0) - (a.href?.length || 0))[0]
}

/**
 * Hook to track which tab is currently active based on the selected link or the current URL
 * Listens for turbo navigation events and updates the active tab accordingly
 */
function useActiveTab(tabs?: LocalNavigationTab[], defaultSelectedLink?: string) {
  const [activeTab, setActiveTab] = useState<LocalNavigationTab | undefined>(() => {
    return findActiveTabBySelectedLink(tabs || [], defaultSelectedLink)
  })

  // Matches the behavior from the Rails listeners in app/assets/modules/github/behaviors/side-navigation.ts
  useEffect(() => {
    function updateActiveTabFromMetaElement(node: HTMLMetaElement) {
      if (node.isConnected && node.getAttribute('name') === 'selected-link') {
        const selection = node.getAttribute('value')
        const selectedLinkTab = findActiveTabBySelectedLink(tabs || [], selection || undefined)
        if (selectedLinkTab) {
          setActiveTab(selectedLinkTab)
          return
        }
      }
    }

    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof HTMLMetaElement) {
            updateActiveTabFromMetaElement(node)
          }
        }
      }
    })

    if (document?.head) {
      observer.observe(document.head, {childList: true})
    }

    const turboEventHandler = () => {
      const selectedLinkElement = document.head?.querySelector<HTMLMetaElement>('meta[name="selected-link"]')
      if (selectedLinkElement) {
        updateActiveTabFromMetaElement(selectedLinkElement)
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('turbo:load', turboEventHandler)
    }

    return () => {
      observer.disconnect()
      window?.removeEventListener('turbo:load', turboEventHandler)
    }
  }, [tabs])

  return activeTab
}

/** Renders the local tabs and other nav items specific to this particular route */
export function LocalNavigation({tabs, selectedLink}: LocalNavigationProps) {
  const activeTab = useActiveTab(tabs, selectedLink)

  if (!tabs || tabs.length === 0) {
    return null
  }

  return (
    <UnderlineNav aria-label="Repository" className={styles.LocalNavigation}>
      {tabs.map(tab => {
        const Icon = getOcticon(tab.icon)
        const href = tab.href || '#'
        const isCurrentPage = activeTab?.id === tab.id

        return (
          <UnderlineNav.Item
            key={tab.id}
            href={href}
            leadingVisual={Icon ? <Icon /> : undefined}
            data-hotkey={tab.hotkey}
            aria-current={isCurrentPage ? 'page' : undefined}
            counter={getTabCount(tab.count)}
            {...getReactNavAttrs(tab.reactNav)}
            {...getTurboNavAttrs(tab.turboNav)}
          >
            {tab.label}
          </UnderlineNav.Item>
        )
      })}
    </UnderlineNav>
  )
}

try{ LocalNavigation.displayName ||= 'LocalNavigation' } catch {}
try{ Icon.displayName ||= 'Icon' } catch {}